import {
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { CardsSphereRequest } from 'types/card/card-sphere';
import { useAddSphereMutation } from 'store/slices/cards/sphere/add-sphere';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    openDialog: boolean;
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
}

const AddCardsSphere: React.FC<Props> = (props: Props) => {
    const { openDialog, setOpenDialog, refetch } = props;
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CardsSphereRequest>();

    const [addSphere, { error, isError, isSuccess, isLoading }] =
        useAddSphereMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const onSubmit = (values: CardsSphereRequest) => {
        addSphere(values);
    };
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Dialog aria-hidden={false} open={openDialog} onClose={closeModal}>
                <Box
                    display={'flex'}
                    justifyContent={'center'}
                    bgcolor={rgba(colors.primary.main, 0.5)}
                >
                    <DialogTitle textTransform={'uppercase'}>
                        ADD NEW SPHERE
                    </DialogTitle>
                    <DialogIconClose handleClose={closeModal} />
                </Box>

                <DialogContent>
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box pt={pxToRem(15)} textAlign={'center'}>
                            <Controller
                                name="sphereTitle"
                                control={control}
                                rules={{ required: 'Title is required' }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        sx={{ width: '80%' }}
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Title"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                            <Box
                                mt={pxToRem(15)}
                                display={'flex'}
                                justifyContent={'center'}
                            >
                                <FormControlLabel
                                    label="In Visible"
                                    control={
                                        <Controller
                                            name="isVisible"
                                            control={control}
                                            defaultValue={openDialog ?? false}
                                            render={({ field }) => (
                                                <Checkbox
                                                    size={'medium'}
                                                    {...field}
                                                    checked={field.value}
                                                    onChange={(e) => {
                                                        const checked =
                                                            e.target.checked;
                                                        field.onChange(checked);
                                                    }}
                                                />
                                            )}
                                        />
                                    }
                                    sx={{
                                        '.MuiFormControlLabel-label': {
                                            marginLeft: pxToRem(10),
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ width: '50%' }}
                        onClick={() => formRef.current?.requestSubmit()}
                        variant={'contained'}
                        loading={isLoading}
                    >
                        Create Card Sphere
                    </Button>
                    <Button
                        sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                        onClick={() => closeModal()}
                        variant={'contained'}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Sphere!'}
            />
        </>
    );
};
export default AddCardsSphere;
